import React from 'react';
import { cn } from '@/lib/utils';

export interface SwitchProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'role'> {
  label?: string;
  labelPosition?: 'left' | 'right';
}

export const Switch = React.forwardRef<HTMLInputElement, SwitchProps>(
  ({ className, label, labelPosition = 'right', checked, id, disabled, onChange, ...props }, ref) => {
    const switchId = id || `switch-${Math.random().toString(36).substr(2, 9)}`;

    // Pull `data-testid` (if any) off the rest props and forward it to the
    // outer <label> rather than the sr-only <input>. The <input> is hidden
    // and the wrapping <label> intercepts pointer events, so Playwright (and
    // any other automation) needs the testid on the actually-clickable
    // element. This eliminates the `xpath=..` / parent-locator workaround
    // every demo spec used to need.
    const { ['data-testid' as const]: dataTestId, ...inputProps } = props as Record<string, unknown>;

    const switchElement = (
      <label
        htmlFor={switchId}
        className="relative inline-block cursor-pointer"
        {...(dataTestId ? { 'data-testid': dataTestId } : {})}
      >
        <input
          ref={ref}
          id={switchId}
          type="checkbox"
          role="switch"
          checked={checked}
          disabled={disabled}
          onChange={onChange}
          className="sr-only peer"
          aria-checked={checked}
          {...inputProps}
        />
        <div
          className={cn(
            'block h-6 w-11 rounded-full transition-colors duration-200',
            'bg-gray-300 dark:bg-gray-600 peer-checked:bg-primary-600',
            'ring-1 ring-inset ring-gray-300/50 peer-checked:ring-0',
            'peer-hover:bg-gray-400 dark:peer-hover:bg-gray-500 peer-checked:peer-hover:bg-primary-700',
            'peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2',
            'peer-disabled:cursor-not-allowed peer-disabled:opacity-50',
            'peer-disabled:peer-hover:bg-muted peer-checked:peer-disabled:peer-hover:bg-primary-600',
            disabled && 'cursor-not-allowed opacity-50',
            className
          )}
        />
        <div
          className={cn(
            'absolute left-[2px] top-[2px] h-5 w-5 rounded-full bg-white transition-transform duration-200 pointer-events-none',
            'peer-checked:translate-x-5',
            'shadow-sm'
          )}
        />
      </label>
    );
    
    if (!label) {
      return switchElement;
    }
    
    return (
      <div className="flex items-center gap-2">
        {labelPosition === 'left' && (
          <label
            htmlFor={switchId}
            className={cn(
              'text-sm font-medium text-foreground cursor-pointer select-none',
              disabled && 'cursor-not-allowed opacity-50'
            )}
          >
            {label}
          </label>
        )}
        {switchElement}
        {labelPosition === 'right' && (
          <label
            htmlFor={switchId}
            className={cn(
              'text-sm font-medium text-foreground cursor-pointer select-none',
              disabled && 'cursor-not-allowed opacity-50'
            )}
          >
            {label}
          </label>
        )}
      </div>
    );
  }
);

Switch.displayName = 'Switch';
